import { cn } from '@/lib';
import { useTranslation } from 'react-i18next';
import { MINUTES_PER_DAY, type WorkScheduleEntry } from './workScheduleParser';

export interface WorkScheduleWeekViewProps {
  schedules: WorkScheduleEntry[];
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const FIRST_WEEKEND_INDEX = 5;
const DEFAULT_GRID_START_MINUTES = 6 * 60;
const DEFAULT_GRID_END_MINUTES = 20 * 60;
// Wide rows show a line every 2h; narrow rows thin that out to every 4h so it stays legible.
const HOUR_TICK_STEP_MINUTES = 2 * 60;
const HOUR_TICK_STEP_MINUTES_SMALL = 4 * 60;
const LANE_HEIGHT_PX = 32;
const ROW_PADDING_PX = 8;
// Hour guide lines are drawn faded so the schedule blocks stay the visual focus.
const GUIDE_LINE_COLOR = 'color-mix(in srgb, var(--color-border) 55%, transparent)';

// One colour set per schedule slot, matching the legend chips and the grid blocks.
const DEFAULT_SCHEDULE_COLOR = { border: 'border-primary', block: 'bg-primary/10 text-primary' };
const SCHEDULE_COLORS = [
  DEFAULT_SCHEDULE_COLOR,
  { border: 'border-tooltip', block: 'bg-tooltip/10 text-tooltip' },
  { border: 'border-warning', block: 'bg-warning/15 text-warning' },
  { border: 'border-secondary', block: 'bg-secondary/10 text-secondary' },
];

// WorkScheduleWeekView renders the read-only week grid: a legend chip per schedule, hour ticks,
// and one row per weekday with the schedule blocks placed by time. Overlapping blocks stack
// into extra lanes so nothing is hidden.
export function WorkScheduleWeekView({ schedules: rawSchedules }: WorkScheduleWeekViewProps) {
  const { t } = useTranslation();
  // Entries without a name get a numbered label so legend and blocks always show something
  const schedules: NamedSchedule[] = rawSchedules.map((schedule, index) => ({
    ...schedule,
    name: schedule.name ?? t('devices.detail.rules.workSchedule.unnamed', { index: index + 1 }),
  }));
  const grid = computeGridBounds(schedules);
  const weekdayLabels = WEEKDAY_KEYS.map((key) => t(`devices.detail.rules.workSchedule.days.${key}`));
  const wideTicks = hourTicks(grid, HOUR_TICK_STEP_MINUTES);
  const narrowTicks = hourTicks(grid, HOUR_TICK_STEP_MINUTES_SMALL);

  return (
    <div className="flex flex-col gap-5">
      <ScheduleLegend schedules={schedules} weekdayLabels={weekdayLabels} />

      {/* Hour ticks above the grid, offset by the weekday label column. Narrow rows show every
          4th hour instead of every 2nd so the labels don't crowd together. */}
      <div>
        <div className="mb-1.5 flex">
          <div className="w-13 flex-none" />
          <div className="relative h-4 flex-1">
            <HourTickLabels ticks={wideTicks} className="hidden sm:block" />
            <HourTickLabels ticks={narrowTicks} className="sm:hidden" />
          </div>
        </div>

        {/* Weekday rows */}
        <div className="border-border-darker overflow-hidden rounded-lg border">
          {weekdayLabels.map((label, dayIndex) => (
            <WeekdayRow
              key={label}
              label={label}
              isWeekend={dayIndex >= FIRST_WEEKEND_INDEX}
              blocks={blocksForDay(schedules, dayIndex, grid)}
              wideGuideSegments={wideTicks.length - 1}
              narrowGuideSegments={narrowTicks.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// A schedule entry whose name has been resolved for display.
type NamedSchedule = WorkScheduleEntry & { name: string };

interface ScheduleLegendProps {
  schedules: NamedSchedule[];
  weekdayLabels: string[];
}

// ScheduleLegend lists each schedule with its colour, days and time range.
function ScheduleLegend({ schedules, weekdayLabels }: ScheduleLegendProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {schedules.map((schedule, index) => (
        <div
          key={schedule.id}
          className={cn(
            'bg-surface border-border rounded-lg border border-l-[3px] px-3.5 py-2 w-50',
            colorFor(index).border
          )}
        >
          <div className="text-onsurface text-sm font-medium">{schedule.name}</div>
          <div className="text-muted text-xs">
            {summarizeDays(schedule.days, weekdayLabels)} · {formatClock(schedule.startMinutes)} – {formatClock(schedule.endMinutes)}
          </div>
        </div>
      ))}
    </div>
  );
}

interface HourTickLabelsProps {
  ticks: { minutes: number; left: string }[];
  className: string;
}

// HourTickLabels renders one set of hour labels ("00h", "02h", …) absolutely positioned along
// the axis. Two instances are stacked and toggled by breakpoint so narrow rows show fewer.
// Every label is centered on its tick, except the last one, which right-aligns to the grid's
// edge instead of overflowing past it.
function HourTickLabels({ ticks, className }: HourTickLabelsProps) {
  return (
    <div className={cn('absolute inset-0', className)}>
      {ticks.map((tick, index) => {
        const isLast = index === ticks.length - 1;
        return (
          <span
            key={tick.minutes}
            className={cn('text-muted absolute text-[11px] whitespace-nowrap', isLast ? 'right-0' : '-translate-x-1/2')}
            style={isLast ? undefined : { left: tick.left }}
          >
            {formatHourTick(tick.minutes)}
          </span>
        );
      })}
    </div>
  );
}

interface WeekdayRowProps {
  label: string;
  isWeekend: boolean;
  blocks: PositionedBlock[];
  // Number of equal segments between the hour ticks, used to draw the vertical guides.
  wideGuideSegments: number;
  narrowGuideSegments: number;
}

// WeekdayRow renders the label column and the time lane with the day's positioned blocks.
function WeekdayRow({ label, isWeekend, blocks, wideGuideSegments, narrowGuideSegments }: WeekdayRowProps) {
  const laneCount = Math.max(1, ...blocks.map((block) => block.lane + 1));
  const rowHeight = laneCount * LANE_HEIGHT_PX + ROW_PADDING_PX * 2;

  return (
    <div className={cn('border-border-darker flex border-b last:border-b-0', isWeekend ? 'bg-surface-darker' : 'bg-surface')}>
      <div
        className={cn(
          'border-border flex w-13 flex-none items-center justify-center border-r text-xs font-medium',
          isWeekend ? 'text-muted' : 'text-onsurface'
        )}
      >
        {label}
      </div>
      {/* Time lane: faded vertical hour guides underneath, narrow rows thinned to every 4th hour */}
      <div className="relative flex-1" style={{ height: rowHeight }}>
        <div
          className="absolute inset-0 hidden sm:block"
          style={{
            backgroundImage: `repeating-linear-gradient(to right, ${GUIDE_LINE_COLOR} 0 1px, transparent 1px calc(100% / ${wideGuideSegments}))`,
          }}
        />
        <div
          className="absolute inset-0 sm:hidden"
          style={{
            backgroundImage: `repeating-linear-gradient(to right, ${GUIDE_LINE_COLOR} 0 1px, transparent 1px calc(100% / ${narrowGuideSegments}))`,
          }}
        />
        {blocks.map((block) => (
          <div
            key={block.id}
            className={cn(
              'absolute flex h-7 items-center gap-2 overflow-hidden rounded-lg border border-l-[3px] px-2.5 text-xs',
              colorFor(block.colorIndex).border,
              colorFor(block.colorIndex).block
            )}
            style={{ top: 12 + block.lane * LANE_HEIGHT_PX, left: block.left, width: block.width }}
            title={`${block.name} ${block.time}`}
          >
            <span className="truncate font-medium">{block.name}</span>
            <span className="opacity-75 whitespace-nowrap">{block.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface GridBounds {
  startMinutes: number;
  endMinutes: number;
}

interface PositionedBlock {
  id: string;
  name: string;
  time: string;
  lane: number;
  colorIndex: number;
  left: string;
  width: string;
}

// computeGridBounds widens the default 06:00–20:00 window to fit every schedule, on full hours.
function computeGridBounds(schedules: WorkScheduleEntry[]): GridBounds {
  const earliest = Math.min(DEFAULT_GRID_START_MINUTES, ...schedules.map((schedule) => schedule.startMinutes));
  const latest = Math.max(DEFAULT_GRID_END_MINUTES, ...schedules.map((schedule) => schedule.endMinutes));
  return {
    startMinutes: Math.floor(earliest / 60) * 60,
    endMinutes: Math.min(MINUTES_PER_DAY, Math.ceil(latest / 60) * 60),
  };
}

function hourTicks(grid: GridBounds, stepMinutes: number) {
  const ticks: { minutes: number; left: string }[] = [];
  for (let minutes = grid.startMinutes; minutes <= grid.endMinutes; minutes += stepMinutes) {
    ticks.push({ minutes, left: percentOf(minutes, grid) });
  }
  return ticks;
}

// blocksForDay positions the schedules active on one weekday and assigns lanes so that
// overlapping blocks never cover each other.
function blocksForDay(schedules: NamedSchedule[], dayIndex: number, grid: GridBounds): PositionedBlock[] {
  const laneEnds: number[] = [];
  return schedules
    .map((schedule, colorIndex) => ({ schedule, colorIndex }))
    .filter(({ schedule }) => schedule.days.includes(dayIndex))
    .sort((a, b) => a.schedule.startMinutes - b.schedule.startMinutes)
    .map(({ schedule, colorIndex }) => {
      let lane = laneEnds.findIndex((laneEnd) => laneEnd <= schedule.startMinutes);
      if (lane < 0) lane = laneEnds.push(schedule.endMinutes) - 1;
      else laneEnds[lane] = schedule.endMinutes;

      const span = grid.endMinutes - grid.startMinutes;
      return {
        id: `${schedule.id}-${dayIndex}`,
        name: schedule.name,
        time: `${formatClock(schedule.startMinutes)} – ${formatClock(schedule.endMinutes)}`,
        lane,
        colorIndex,
        left: percentOf(schedule.startMinutes, grid),
        width: `${((schedule.endMinutes - schedule.startMinutes) / span) * 100}%`,
      };
    });
}

// summarizeDays shortens common patterns (Mon – Fri, Sat, Sun) and lists the rest.
function summarizeDays(days: number[], weekdayLabels: string[]): string {
  const labelOf = (day: number | undefined) => (day === undefined ? '' : weekdayLabels[day] ?? '');
  const isConsecutive = days.every((day, index) => index === 0 || day === (days[index - 1] ?? -1) + 1);
  if (isConsecutive && days.length > 2) {
    return `${labelOf(days[0])} – ${labelOf(days[days.length - 1])}`;
  }
  return days.map(labelOf).join(', ');
}

function percentOf(minutes: number, grid: GridBounds): string {
  return `${((minutes - grid.startMinutes) / (grid.endMinutes - grid.startMinutes)) * 100}%`;
}

function colorFor(index: number) {
  return SCHEDULE_COLORS[index % SCHEDULE_COLORS.length] ?? DEFAULT_SCHEDULE_COLOR;
}

function formatClock(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

// formatHourTick labels the grid's hour axis, e.g. "00h", "14h" (ticks always land on full hours).
function formatHourTick(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}h`;
}
